import React from "react";
import { motion } from "framer-motion";
import { Plus, Sparkles } from "lucide-react";
import { Button } from "../ui/button";
import { useCart, formatEUR } from "../../context/CartContext";
import { toast } from "sonner";

export default function ProductCard({ product }) {
  const { addItem } = useCart();
  const isBestDeal = product.badge === "Bester Deal";

  const onAdd = () => {
    addItem(product);
    toast.success(`${product.name} in den Warenkorb gelegt.`);
  };

  return (
    <motion.article
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.6, ease: "easeOut" }}
      className={`group relative flex flex-col rounded-3xl border bg-white p-6 sm:p-7 transition-all duration-500 hover:-translate-y-1 hover:shadow-[0_30px_60px_-30px_rgba(225,29,72,0.25)] ${
        isBestDeal
          ? "border-qh-rose/40 shadow-[0_20px_40px_-20px_rgba(225,29,72,0.25)]"
          : "border-qh-line"
      }`}
      data-testid={`product-${product.id}`}
    >
      {product.badge && (
        isBestDeal ? (
          <span
            className="absolute -top-3 right-5 z-10 inline-flex items-center gap-1.5 rounded-full bg-qh-rose text-white text-[11px] font-semibold uppercase tracking-widest px-3.5 py-1.5 shadow-[0_10px_25px_-5px_rgba(225,29,72,0.55)] ring-2 ring-white"
            data-testid={`product-${product.id}-badge`}
          >
            <Sparkles className="w-3 h-3 fill-white" strokeWidth={0} />
            {product.badge}
          </span>
        ) : (
          <span
            className="absolute top-5 right-5 rounded-full bg-qh-rose text-white text-[10px] font-semibold uppercase tracking-widest px-3 py-1"
            data-testid={`product-${product.id}-badge`}
          >
            {product.badge}
          </span>
        )
      )}

      <div className="relative aspect-square rounded-2xl bg-[#FFFBFB] overflow-hidden border border-[#FFE4E6]">
        <img
          src="/assets/qheart-front.png"
          alt={product.name}
          draggable="false"
          className="absolute inset-0 w-full h-full object-contain p-6 transition-transform duration-500 group-hover:scale-105"
          style={{ filter: "drop-shadow(0 20px 25px rgba(225,29,72,0.2))" }}
        />
        <span className="absolute bottom-3 left-3 rounded-full bg-white/90 backdrop-blur border border-qh-line text-[10px] font-semibold uppercase tracking-widest text-qh-ink px-3 py-1">
          × {product.count}
        </span>
      </div>

      <div className="mt-6 flex-1">
        <h3 className="font-display text-2xl font-medium tracking-tight text-qh-ink">
          {product.name}
        </h3>
        <p className="mt-2 text-sm text-qh-slate leading-relaxed">
          {product.tagline}
        </p>
        <p className="mt-3 text-xs text-qh-slate">
          Dein Schlüssel zu echten Begegnungen.
        </p>
      </div>

      <div className="mt-6 flex items-center justify-between gap-3">
        <div>
          <div className="font-display text-2xl font-semibold text-qh-ink">
            {formatEUR(product.price)}
          </div>
          <div className="text-[11px] text-qh-slate">
            {formatEUR(product.price / product.count)} / QHeart
          </div>
        </div>
        <Button
          onClick={onAdd}
          className="rounded-full bg-qh-rose hover:bg-qh-rosedark text-white px-5 h-10 font-medium transition-all hover:-translate-y-0.5"
          data-testid={`product-${product.id}-add`}
        >
          <Plus className="w-4 h-4 mr-1.5" strokeWidth={2} />
          In den Warenkorb
        </Button>
      </div>
    </motion.article>
  );
}
